import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { Router, ActivatedRoute, Params } from '@angular/router';

import { AuthService } from '../../services/auth.service';
import { CandidateService } from '../../services/candidate.service';
import { CompanyService } from '../../services/company.service';
import { JobService } from '../../services/job.service';
import { ToolService } from '../../services/tool.service';

import { ICandidate } from '../../models/ICandidate.model';
import { ICompanyContact } from '../../models/ICompanyContact.model';
import { IJob } from '../../models/IJob.model';
import { User } from '../../models/User.model';

@Component({
    selector: 'app-jobs',
    templateUrl: './job.component.html',
    styleUrls: ['./job.component.less']
})
export class JobComponent implements OnInit {
    private user: User;
    private currentJobOrderId: number;
    private candidateType: any[] = [
        { id: 'New', name: 'New Candidates' },
        { id: 'Viewed', name: 'Viewed Candidates' },
        { id: 'InProcess', name: 'In Process Candidates' },
        { id: 'Dismissed', name: 'Dismissed Candidates' }
    ];

    jobs: IJob[];
    companyName: string;
    showingCandidates = false;
    candidateInterview: ICandidate = null;
    candidateDismiss: ICandidate = null;
    contact: ICompanyContact = null;

    constructor(
        private _auth: AuthService,
        private _candidate: CandidateService,
        private _company: CompanyService,
        private _job: JobService,
        private _location: Location,
        private _route: ActivatedRoute,
        private _router: Router,
        private _tools: ToolService
    ) { }

    private getJobs(userId: number): void {
        this._job
            .getJobs(userId)
            .subscribe((jobs: IJob[]) => {
                this.jobs = jobs;

                this.currentJobOrderId = +this._route.snapshot.paramMap.get('jobOrderId');
                if (this.currentJobOrderId) {
                    this.expandJob(this.jobs.find(job => job.JobOrderId === this.currentJobOrderId));
                } else if (this.jobs.length === 1) {
                    this.expandJob(this.jobs[0]);
                }

                if (this.jobs.length) {
                    this.getContactInfo(this.jobs[0].DefaultCompanyContactId);
                }
            });
    }

    private getCandidates(job: IJob): void {
        this._candidate
            .getCandidates(job.JobOrderId)
            .then(candidates => {
                const sortedCandidates: ICandidate[][] = [];

                for (let i = 0; i < this.candidateType.length; i++) {
                    const candidateType: string = this.candidateType[i].id;
                    sortedCandidates[i] = candidates.filter(function (candidate) {
                        return candidate.TabStatus === candidateType;
                    });
                }

                job.Candidates = candidates;
                job.SortedCandidates = sortedCandidates;
            });
    }

    private getContactInfo(companyContactId: number): void {
        let contactRequest;

        const impersonationData = this._auth.getImpersonationData();
        if (impersonationData) {
            contactRequest = this._company.recruiterGetContact(impersonationData.ImpersonatedId);
        } else {
            contactRequest = this._company.getContact(companyContactId);
        }

        contactRequest
            .subscribe(contact => {
                this.contact = contact;
            });
    }

    private updateUrl(jobOrderId?: number): void {
        if (jobOrderId) {
            this._location.go(`/jobs/${jobOrderId.toString()}`);
        } else {
            this._location.go('/jobs');
        }
    }

    expandJob(job: IJob): void {
        if (job.Expanded) {
            job.Expanded = this.showingCandidates = false;
            this.currentJobOrderId = null;
            this.updateUrl();
        } else {
            this.closeAll();
            job.Expanded = this.showingCandidates = true;
            this.currentJobOrderId = job.JobOrderId;
            this.updateUrl(job.JobOrderId);
        }

        if (job.Expanded && !job.SortedCandidates) {
            this.getCandidates(job);
        }
    }

    closeAll(): void {
        this.jobs.forEach(function (thisJob) {
            thisJob.Expanded = false;
        });
        this.updateUrl();
        this.showingCandidates = false;
        this.candidateInterview = null;
    }

    goToCandidate(candidate: ICandidate, companyContactId: number): void {
        if (candidate.TabStatus === 'New') {
            this._candidate.setAppToViewed(candidate.JobAppId, companyContactId);
        }

        this._router.navigate(['/candidate', candidate.JobAppId, candidate.CandidateId]);
    }

    interviewCandidate(candidate: ICandidate, companyContactId: number): void {
        if (this.candidateDismiss) {
            this.candidateDismissed(false);
        }

        window.scrollTo(0, 0);
        this.candidateInterview = candidate;
    }

    interviewRequestSent(success: boolean): void {
        if (success) {
            this.getCandidates(this.jobs.find(job => job.JobOrderId === this.currentJobOrderId));

            this.showInterviewMessage();
        }

        this.candidateInterview = null;
    }

    dismissCandidate(candidate: ICandidate, companyContactId: number): void {
        if (this.candidateInterview) {
            this.interviewRequestSent(false);
        }

        window.scrollTo(0, 0);
        this.candidateDismiss = candidate;
    }

    candidateDismissed(success: boolean): void {
        if (success) {
            const job = this.jobs.find(x => x.JobOrderId === this.currentJobOrderId);

            let found = false;
            for (let i = 0; i <= 2; i++) {
                if (found) break;
                for (let j = 0; j < job.SortedCandidates[i].length; j++) {
                    if (job.SortedCandidates[i][j].JobAppId === this.candidateDismiss.JobAppId) {
                        job.SortedCandidates[i][j].TabStatus = 'Dismissed';
                        job.SortedCandidates[i][j].Status = '';
                        job.SortedCandidates[3].push(job.SortedCandidates[i][j]);
                        job.SortedCandidates[i].splice(j, 1);
                        found = true;
                    }
                }
            }

            this.showDismissedMessage();
        }

        this.candidateDismiss = null;
    }

    showInterviewMessage(): void {
        const alert = document.getElementById('alert-interview');

        if (alert) {
            alert.classList.add('show');
        }
    }

    showDismissedMessage(): void {
        const alert = document.getElementById('alert-dismissed');

        if (alert) {
            alert.classList.add('show');
        }
    }

    sendCandidates(): void {
        alert(`Send more candidates to ${this.currentJobOrderId}`);
    }

    showCreatedMessage(): void {
        const alert = document.getElementById('alert-new-created');

        if (alert) {
            alert.classList.add('show');
        }
    }

    ngOnInit() {
        this.user = this._auth.getUser();
        const impersonated = this._auth.getImpersonationData();

        if (impersonated) {
            this.getJobs(impersonated.ImpersonatedId);
        } else {
            this.getJobs(this.user.Id);
        }

        this._route.queryParams.subscribe((params: Params) => {
            if (params['i']) {
                this.showCreatedMessage();
            } else if (params['r']) {
                this.showInterviewMessage();
            } else if (params['d']) {
                this.showDismissedMessage();
            }
        });
    }
}



// WEBPACK FOOTER //
// ./src/app/components/job/job.component.ts